import { Directive, Type, computed, input, reflectComponentType } from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'

import { EMPTY, Observable, switchMap } from 'rxjs'

import { deprecatedAlias } from '@flowx/core-sdk'

import type { FlxCustomComponentData } from './tree-node.component'

// The inputs one custom component receives. Sits beside it in the tree node's loop, so the
// `input` subscription lives exactly as long as the component it feeds.
@Directive({
  selector: '[flxCustomComponentInputs]',
  exportAs: 'flxCustomComponentInputs',
})
export class FlxCustomComponentInputsDirective {
  readonly #declaredInputs = computed(
    () =>
      new Set(
        reflectComponentType(this.component())?.inputs.map(({ templateName }) => templateName)
      )
  )
  readonly #takesInput = computed(() => this.#declaredInputs().has('input'))
  readonly #takesData = computed(() => this.#declaredInputs().has('data$'))

  // Subscribed only for a component that declares `input`.
  readonly #input = toSignal(
    toObservable(computed(() => (this.#takesInput() ? this.data$() : EMPTY))).pipe(
      switchMap((data$) => data$)
    )
  )

  readonly component = input.required<Type<unknown>>({ alias: 'flxCustomComponentInputs' })
  readonly data$ = input.required<Observable<FlxCustomComponentData>>()

  // `input` matches React's `input` prop. A component that still declares `data$` gets that
  // Observable too, with a dev warning; one declaring neither keeps the old `data$` hand-off.
  readonly inputs = computed<Record<string, unknown>>(() => {
    const takesInput = this.#takesInput()
    const takesData = this.#takesData()
    return {
      ...(takesInput && { input: this.#input() }),
      ...(takesData && { data$: deprecatedAlias('data$', 'input', this.data$)() }),
      ...(!takesInput && !takesData && { data$: this.data$() }),
    }
  })
}
